import React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import InputDate from "../grossesse/InputDate";
import DateLine from "../grossesse/DateLine";
import ChildrenButton from "../conge-maternite/ChildrenButton";
import { dailyAllowance, IJ_YEAR } from "../conge-maternite/congeRules";
import { MAX_HOSPITAL_DAYS, MIN_PERIOD, paterniteDays, plan } from "./paterniteRules";

const parse = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);
const valid = (d) => d instanceof Date && !isNaN(d) && d.getFullYear() >= 2000 && d.getFullYear() <= 2035;
const short = (d) => format(d, "EEE d MMM yyyy", { locale: fr });
const euros = (n) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fieldCls = "dropdown m-0 min-w-32";
const numCls = "dropdown m-0 min-w-0 text-center";

export default function CongePaternite() {
    const [birth, setBirth] = React.useState("");
    const [babies, setBabies] = React.useState(1);
    const [naissanceStart, setNaissanceStart] = React.useState("birth");
    const [hospital, setHospital] = React.useState("");
    const [mode, setMode] = React.useState("immediate");
    const [p1Start, setP1Start] = React.useState("");
    const [p1Days, setP1Days] = React.useState("10");
    const [p2Start, setP2Start] = React.useState("");
    const [salary, setSalary] = React.useState("");

    const b = parse(birth);
    const multiple = babies > 1;
    const r = valid(b)
        ? plan({ birth: b, multiple, naissanceStart, hospitalDays: Number(hospital) || 0, mode, p1Start: parse(p1Start), p1Days: Number(p1Days), p2Start: parse(p2Start) })
        : null;
    const ij = dailyAllowance(parseFloat(String(salary).replace(",", ".")));

    return (
        <>
            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-col gap-4">
                <InputDate inputDate={birth} handleInputDateChange={(e) => setBirth(e.target.value)}
                    label="Date de naissance (ou date prévue)" hint="La date de naissance, ou d'accueil de l'enfant." />
                <div>
                    <p className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Naissance</p>
                    <div className="flex gap-2 sm:gap-4 items-stretch">
                        <ChildrenButton number={1} onClick={setBabies} isSelected={babies === 1} />
                        <ChildrenButton number={2} onClick={setBabies} isSelected={babies === 2} />
                        <ChildrenButton number={3} onClick={setBabies} isSelected={babies >= 3} />
                    </div>
                </div>
                <label className="flex flex-col gap-1 text-sm">
                    <span className="font-semibold text-gray-900">Début du congé de naissance (3 jours)</span>
                    <select className="dropdown-filter select" value={naissanceStart} onChange={(e) => setNaissanceStart(e.target.value)}>
                        <option value="birth">Le jour de la naissance</option>
                        <option value="next">Le 1er jour ouvrable suivant</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                    <span className="font-semibold text-gray-900">Organisation des {paterniteDays(multiple) - 4} jours restants</span>
                    <select className="dropdown-filter select" value={mode} onChange={(e) => setMode(e.target.value)}>
                        <option value="immediate">D'affilée, juste après les 4 jours obligatoires</option>
                        <option value="later">En une fois, plus tard</option>
                        <option value="split">En deux fois</option>
                    </select>
                </label>
                {mode !== "immediate" && (
                    <div className="flex flex-wrap gap-4 text-sm">
                        <label className="flex flex-col gap-1"><span>{mode === "split" ? "Début de la 1re période" : "Début de la période"}</span>
                            <input type="date" className={fieldCls} value={p1Start} min="2000-01-01" max="2035-12-31" onChange={(e) => setP1Start(e.target.value)} /></label>
                        {mode === "split" && (
                            <>
                                <label className="flex flex-col gap-1"><span>Durée de la 1re période</span>
                                    <span className="flex items-center gap-2">
                                        <input type="text" inputMode="numeric" className={`${numCls} w-16`} value={p1Days}
                                            onChange={(e) => setP1Days(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))} />
                                        <span>jours</span>
                                    </span></label>
                                <label className="flex flex-col gap-1"><span>Début de la 2e période</span>
                                    <input type="date" className={fieldCls} value={p2Start} min="2000-01-01" max="2035-12-31" onChange={(e) => setP2Start(e.target.value)} /></label>
                            </>
                        )}
                    </div>
                )}
                <details className="text-sm">
                    <summary className="cursor-pointer font-semibold">Options : hospitalisation, indemnités</summary>
                    <div className="flex flex-col gap-3 mt-3">
                        <label className="flex flex-col gap-1">
                            <span>Jours d'hospitalisation du bébé dès la naissance (max. {MAX_HOSPITAL_DAYS})</span>
                            <input type="text" inputMode="numeric" className={`${numCls} w-20`} value={hospital} placeholder="0"
                                onChange={(e) => setHospital(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))} />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span>Salaire brut mensuel moyen des 3 derniers mois (€)</span>
                            <input type="text" inputMode="decimal" className={`${numCls} w-28`} value={salary} placeholder="ex. 2500"
                                onChange={(e) => setSalary(e.target.value.replace(/[^0-9.,]/g, ""))} />
                        </label>
                    </div>
                </details>
            </div>

            {!r ? (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Renseignez la date de naissance pour calculer vos dates</p>
                    <p className="text-gray-600 text-sm mt-2">Vous obtiendrez les dates du congé de naissance, des 4 jours obligatoires et des jours restants, avec la date limite pour les prendre.</p>
                </div>
            ) : (
                <>
                    <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">Résultats</div>
                    <div className="p-4 my-2 sm:my-4 bg-teal-700 rounded-lg text-white">
                        <p className="text-lg sm:text-xl font-bold mb-3">{r.totalLeave} jours de congé <span className="text-sm font-normal">(3 jours de naissance + {r.totalPaternite} jours de paternité)</span></p>
                        <DateLine description="Congé de naissance (3 jours ouvrables)" date={format(r.naissance.start, "dd/MM")} endDate={format(r.naissance.end, "dd/MM/yyyy")} />
                        <DateLine description="Paternité : 4 jours obligatoires" date={format(r.mandatory.start, "dd/MM")} endDate={format(r.mandatory.end, "dd/MM/yyyy")} />
                        {r.hospital && <DateLine description={`Hospitalisation du bébé (${r.hospital.days} j)`} date={format(r.hospital.start, "dd/MM")} endDate={format(r.hospital.end, "dd/MM/yyyy")} />}
                        {r.periods.map((p, i) => (
                            <DateLine key={i} description={`Paternité : ${r.periods.length > 1 ? `période ${i + 1}` : "jours restants"} (${p.days} j)`} date={format(p.start, "dd/MM")} endDate={format(p.end, "dd/MM/yyyy")} />
                        ))}
                        <DateLine description="À prendre au plus tard le" date={short(r.deadline)} />
                    </div>

                    {r.errors.length > 0 && (
                        <div className="p-3 my-2 bg-red-50 border border-red-300 rounded-lg text-sm text-red-800">
                            <ul className="list-disc pl-4">{r.errors.map((e) => <li key={e}>{e}</li>)}</ul>
                        </div>
                    )}

                    <div className="p-3 my-2 sm:my-4 bg-stone-100 rounded-lg text-sm text-gray-700">
                        <p className="font-semibold text-gray-900 mb-1">À savoir</p>
                        <ul className="list-disc pl-4">
                            <li>Prévenez votre employeur au moins 1 mois avant la date prévue de la naissance{r.notices.length > 0 && mode !== "immediate" ? <>, et au plus tard le {r.notices.map((n) => format(n, "dd/MM/yyyy")).join(" puis le ")} pour {r.notices.length > 1 ? "chaque période" : "la période"} suivante{r.notices.length > 1 ? "s" : ""}</> : null}.</li>
                            <li>Les jours de paternité sont des jours calendaires : week-ends et jours fériés comptent. Chaque période fractionnée dure au moins {MIN_PERIOD} jours.</li>
                            <li>Depuis le 1er juillet 2026, le congé supplémentaire de naissance (1 ou 2 mois) peut suivre le congé de paternité.</li>
                        </ul>
                    </div>

                    {ij > 0 && (
                        <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-emerald-900 to-emerald-700 text-white text-center">
                            <div className="text-sm text-emerald-200">Indemnités journalières paternité estimées ({IJ_YEAR})</div>
                            <div className="text-3xl sm:text-4xl font-bold my-1">{euros(ij)} <span className="text-base font-semibold">/ jour</span></div>
                            <div className="text-sm">soit {euros(ij * r.totalPaternite)} sur {r.totalPaternite} jours, nets de CSG-CRDS</div>
                            <div className="text-xs text-emerald-100 mt-1">Les 3 jours du congé de naissance sont payés par l'employeur, comme des jours travaillés.</div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
